/**
 * ⚠️ ARQUIVO PROTEGIDO
 * 
 * Todas as chamadas relacionadas a tenants do Super Admin ficam aqui.
 * Tokens e dados sensíveis protegidos.
 */

import type {
  TenantsListResponse,
  TenantsFilters,
  TenantDetails,
  TenantDispatchesTimelineResponse
} from '../../types/superadmin/tenant.types';

class TenantsApiService {
  private readonly baseUrl = '/api/superadmin/tenants';

  /**
   * Helper privado para fazer requisições autenticadas
   * Tokens ficam protegidos aqui
   */
  private async authenticatedFetch(url: string, options: RequestInit = {}): Promise<Response> {
    const token = localStorage.getItem('auth_token');
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  }

  /**
   * Lista todos os tenants com filtros
   */
  async getTenants(filters: TenantsFilters = {}): Promise<TenantsListResponse> {
    const queryParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });

    const queryString = queryParams.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    const response = await this.authenticatedFetch(url);
    
    if (!response.ok) {
      throw new Error('Erro ao carregar lista de tenants');
    }
    
    return response.json();
  }

  /**
   * Obtém detalhes completos de um tenant
   * @param tenantId - ID do tenant
   */
  async getTenantDetails(tenantId: string): Promise<TenantDetails> {
    const response = await this.authenticatedFetch(
      `${this.baseUrl}/${tenantId}/details`
    );
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Tenant não encontrado');
      }
      throw new Error('Erro ao carregar detalhes do tenant');
    }
    
    return response.json();
  }

  /**
   * Obtém timeline de disparos do tenant
   * @param tenantId - ID do tenant
   * @param days - Número de dias (padrão: 30)
   */
  async getTenantDispatchesTimeline(
    tenantId: string,
    days: number = 30
  ): Promise<TenantDispatchesTimelineResponse> {
    const response = await this.authenticatedFetch(
      `${this.baseUrl}/${tenantId}/dispatches-timeline?days=${days}`
    );
    
    if (!response.ok) {
      throw new Error('Erro ao carregar timeline do tenant');
    }
    
    return response.json();
  }
}

// Exportar instância singleton
export const tenantsApi = new TenantsApiService();
export default tenantsApi;

